import { useEffect, useState } from "react";
import { Tent, Users, Award, Shield, Sparkles, ArrowRight, Mountain, Star, Compass } from "lucide-react";
import { Hero } from "@/components/Hero";
import { ZeltCard } from "@/components/ZeltCard";
import { GuideCard } from "@/components/GuideCard";
import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSeo, generateWebsiteSchema, generateOrganizationSchema } from "@/hooks/useSeo";
import { cn } from "@/lib/utils";
import type { Zelt } from "@/data/zelte";
import type { Guide } from "@/data/guides";
import { kategorien, zelte } from "@/data/zelte";
import { guides } from "@/data/guides";

interface HomeProps {
  onNavigate: (path: string) => void;
  onToggleVergleich: (zelt: Zelt) => void;
  vergleichIds: string[];
}

const fallbackFeatured = zelte.filter(z => z.tags.includes("bestseller") || z.tags.includes("empfohlen")).slice(0, 6);
const fallbackGuides = guides.filter(guide => guide.featured).slice(0, 3);

export function Home({ onNavigate, onToggleVergleich, vergleichIds }: HomeProps) {
  const [featured, setFeatured] = useState<Zelt[]>([]);
  const [featuredGuides, setFeaturedGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [guidesLoading, setGuidesLoading] = useState(true);

  useSeo({
    title: "zelt.kaufen - Zelte vergleichen & das perfekte Zelt finden",
    description: `Vergleichen Sie ${zelte.length} Zeltmodelle in ${kategorien.length} Kategorien. Finden Sie das perfekte Zelt für Camping, Trekking oder Festivals. Unabhängige Bewertungen & Preisvergleich.`,
    keywords: "Zelt kaufen, Zeltvergleich, Camping Zelt, Trekkingzelt, Familienzelt",
    type: "website",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateWebsiteSchema(),
        generateOrganizationSchema(),
      ],
    },
  });

  useEffect(() => {
    fetch("/api/featured")
      .then(res => res.json())
      .then(data => {
        setFeatured(Array.isArray(data) && data.length > 0 ? data : fallbackFeatured);
        setLoading(false);
      })
      .catch(() => {
        setFeatured(fallbackFeatured);
        setLoading(false);
      });

    fetch("/api/guides/featured")
      .then(res => res.json())
      .then(data => {
        setFeaturedGuides(Array.isArray(data) && data.length > 0 ? data : fallbackGuides);
        setGuidesLoading(false);
      })
      .catch(() => {
        setFeaturedGuides(fallbackGuides);
        setGuidesLoading(false);
      });
  }, []);

  // Category icons mapping
  const categoryIcons: Record<string, React.ReactNode> = {
    kuppelzelt: <Tent className="w-6 h-6" />,
    tunnelzelt: <Mountain className="w-6 h-6" />,
    familienzelt: <Users className="w-6 h-6" />,
    trekkingzelt: <Sparkles className="w-6 h-6" />,
    geodaet: <Shield className="w-6 h-6" />,
    tipi: <Tent className="w-6 h-6" />,
    wurfzelt: <Star className="w-6 h-6" />,
    dachzelt: <Mountain className="w-6 h-6" />,
  };

  const categoryColors: Record<string, string> = {
    kuppelzelt: "from-green-500/20 to-emerald-500/20 group-hover:from-green-500/30 group-hover:to-emerald-500/30",
    tunnelzelt: "from-blue-500/20 to-indigo-500/20 group-hover:from-blue-500/30 group-hover:to-indigo-500/30",
    familienzelt: "from-amber-500/20 to-orange-500/20 group-hover:from-amber-500/30 group-hover:to-orange-500/30",
    trekkingzelt: "from-purple-500/20 to-violet-500/20 group-hover:from-purple-500/30 group-hover:to-violet-500/30",
    geodaet: "from-rose-500/20 to-pink-500/20 group-hover:from-rose-500/30 group-hover:to-pink-500/30",
    tipi: "from-cyan-500/20 to-sky-500/20 group-hover:from-cyan-500/30 group-hover:to-sky-500/30",
    wurfzelt: "from-teal-500/20 to-emerald-500/20 group-hover:from-teal-500/30 group-hover:to-emerald-500/30",
    dachzelt: "from-slate-500/20 to-zinc-500/20 group-hover:from-slate-500/30 group-hover:to-zinc-500/30",
  };

  return (
    <>
      {/* Hero */}
      <Hero onNavigate={onNavigate} />

      {/* Zelt-Finder Teaser */}
      <section className="py-16">
        <Container>
          <div className="relative overflow-hidden rounded-3xl border bg-card/60 p-8 md:p-12">
            <div className="absolute inset-0 mesh-gradient opacity-20" />
            <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
                  <Compass className="w-4 h-4" />
                  In 4 Fragen zum Modell
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">Welches Zelt passt zu Ihnen?</h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6 max-w-xl">
                  Personenanzahl, Einsatz, Budget und Priorität – der Zelt-Finder sortiert den Katalog und erklärt, warum ein Modell passt.
                </p>
                <Button
                  size="lg"
                  onClick={() => onNavigate("/finder")}
                  className="h-12 px-6 rounded-xl gap-2"
                >
                  Zelt-Finder starten
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {["Personen", "Einsatz", "Budget", "Priorität"].map((step, i) => (
                  <div key={step} className="rounded-2xl bg-background/80 border p-4">
                    <div className="text-xs font-semibold text-primary mb-1">Schritt {i + 1}</div>
                    <div className="font-semibold">{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Featured Zelte */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 mesh-gradient opacity-20" />

        <Container className="relative">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
                <Sparkles className="w-4 h-4" />
                Bestseller
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">Beliebte Zelte</h2>
              <p className="text-muted-foreground text-lg">Unsere Top-Empfehlungen für Ihr nächstes Abenteuer</p>
            </div>
            <Button
              variant="outline"
              onClick={() => onNavigate("/zelte")}
              className="self-start md:self-auto h-11 px-5 rounded-xl gap-2 group"
            >
              Alle Zelte
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border bg-card overflow-hidden">
                  <div className="aspect-[4/3] bg-muted shimmer" />
                  <div className="p-5 space-y-4">
                    <div className="h-4 bg-muted rounded-full w-1/3 shimmer" />
                    <div className="h-6 bg-muted rounded-full w-3/4 shimmer" />
                    <div className="h-4 bg-muted rounded-full w-full shimmer" />
                    <div className="h-4 bg-muted rounded-full w-2/3 shimmer" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((zelt, index) => (
                <div
                  key={zelt.id}
                  className="slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <ZeltCard
                    zelt={zelt}
                    onNavigate={onNavigate}
                    onToggleVergleich={onToggleVergleich}
                    isInVergleich={vergleichIds.includes(zelt.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* Featured Guides */}
      <section className="py-20 bg-muted/30">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
                <Award className="w-4 h-4" />
                Expertenwissen
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">Camping Ratgeber</h2>
              <p className="text-muted-foreground text-lg">Tipps & Tricks für Ihr nächstes Outdoor-Abenteuer</p>
            </div>
            <Button
              variant="outline"
              onClick={() => onNavigate("/ratgeber")}
              className="self-start md:self-auto h-11 px-5 rounded-xl gap-2 group"
            >
              Alle Ratgeber
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          {guidesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-2xl border bg-card overflow-hidden">
                  <div className="aspect-[16/9] bg-muted shimmer" />
                  <div className="p-5 space-y-4">
                    <div className="h-4 bg-muted rounded-full w-1/3 shimmer" />
                    <div className="h-6 bg-muted rounded-full w-3/4 shimmer" />
                    <div className="h-4 bg-muted rounded-full w-full shimmer" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredGuides.map((guide, index) => (
                <div
                  key={guide.id}
                  className="slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <GuideCard guide={guide} onNavigate={onNavigate} />
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* Kategorien */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 mesh-gradient opacity-20" />

        <Container className="relative">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
              <Tent className="w-4 h-4" />
              Kategorien
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Finden Sie Ihr perfektes Zelt</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Von ultraleichten Trekkingzelten bis hin zu geräumigen Familienzelten - entdecken Sie unsere Kategorien
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {kategorien.slice(0, 8).map((kategorie, index) => (
              <Card
                key={kategorie.id}
                className={cn(
                  "group cursor-pointer border-0 overflow-hidden",
                  "bg-card/50 backdrop-blur-sm",
                  "shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]",
                  "hover:shadow-[0_8px_24px_-4px_rgba(0,0,0,0.1)]",
                  "transition-all duration-500 ease-out",
                  "hover:-translate-y-1",
                  "slide-up"
                )}
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => {
                  const slugMap: Record<string, string> = {
                    kuppelzelt: "kuppelzelte",
                    tunnelzelt: "tunnelzelte",
                    familienzelt: "familienzelte",
                    trekkingzelt: "trekkingzelte",
                    geodaet: "geodaet-zelte",
                    tipi: "tipi-zelte",
                    wurfzelt: "wurfzelte",
                    dachzelt: "dachzelte",
                  };
                  onNavigate(`/${slugMap[kategorie.id] || kategorie.id}`);
                }}
              >
                <CardContent className="p-6 text-center">
                  <div className={cn(
                    "w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center",
                    "bg-gradient-to-br transition-all duration-300",
                    "group-hover:scale-110",
                    categoryColors[kategorie.id] || "from-primary/20 to-primary/10"
                  )}>
                    <span className="text-primary">
                      {categoryIcons[kategorie.id] || <Tent className="w-6 h-6" />}
                    </span>
                  </div>
                  <h3 className="font-bold mb-2 group-hover:text-primary transition-colors">
                    {kategorie.name}
                  </h3>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {kategorie.beschreibung}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {/* Why Us */}
      <section className="py-20 bg-muted/30">
        <Container>
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
              <Star className="w-4 h-4" />
              Warum wir
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Warum zelt.kaufen?</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Wir helfen Ihnen, das beste Zelt für Ihre Bedürfnisse zu finden
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Unabhängige Tests",
                description: "Alle Zelte werden von uns unabhängig getestet und bewertet. Keine gesponserten Inhalte.",
                color: "from-green-500/20 to-emerald-500/20",
              },
              {
                icon: Award,
                title: "Experten-Wissen",
                description: "Unser Team besteht aus erfahrenen Outdoor-Enthusiasten mit jahrelanger Camping-Erfahrung.",
                color: "from-amber-500/20 to-orange-500/20",
              },
              {
                icon: Users,
                title: "Community-Bewertungen",
                description: "Echte Bewertungen von echten Campern helfen Ihnen bei der Entscheidung.",
                color: "from-blue-500/20 to-indigo-500/20",
              },
            ].map((item, index) => (
              <div
                key={item.title}
                className="slide-up text-center"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={cn(
                  "w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center",
                  "bg-gradient-to-br transition-all duration-300 hover:scale-110",
                  item.color
                )}>
                  <item.icon className="w-10 h-10 text-foreground" />
                </div>
                <h3 className="font-bold text-xl mb-3">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 mesh-gradient opacity-30" />

        <Container className="relative">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-emerald-600" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.2)_0%,transparent_50%)]" />

            <div className="relative p-10 md:p-16 text-center">
              <div className="inline-flex items-center gap-2 text-sm font-medium text-white/90 mb-4 bg-white/10 rounded-full px-4 py-1.5">
                <Sparkles className="w-4 h-4" />
                Starten Sie jetzt
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
                Bereit für Ihr nächstes Abenteuer?
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
                Vergleichen Sie jetzt die besten Zelte und finden Sie das perfekte Modell für Ihre Outdoor-Aktivitäten.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() => onNavigate("/zelte")}
                  className="h-14 px-8 rounded-xl bg-white text-primary hover:bg-white/90 transition-all duration-300 hover:scale-105 shadow-xl"
                >
                  Zelte entdecken
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => onNavigate("/vergleich")}
                  className="h-14 px-8 rounded-xl bg-transparent border-2 border-white/30 text-white hover:bg-white/10 transition-all duration-300"
                >
                  Vergleich starten
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
